'use client';

import React, { useState } from 'react';
import Link from 'next/link';

interface Product {
  id: number;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  alt: string;
  category: string;
  badge?: string;
  rating: number;
  reviews: number;
  description: string;
}

interface CartItem extends Product {
  quantity: number;
}

const products: Product[] = [
  {
    id: 1,
    name: 'ProShield 4K Dome Camera',
    price: 129.99,
    originalPrice: 179.99,
    image: '/assets/images/cctv-camera-product.png',
    alt: 'ProShield 4K dome security camera with night vision and wide angle lens',
    category: 'CCTV Cameras',
    badge: 'Best Seller',
    rating: 4.8,
    reviews: 342,
    description: '4K Ultra HD, 180° wide angle, night vision up to 100ft, weatherproof IP67',
  },
  {
    id: 2,
    name: 'VaultCam Outdoor Bullet',
    price: 89.99,
    image: '/assets/images/cctv-camera-product.png',
    alt: 'VaultCam outdoor bullet security camera for perimeter surveillance',
    category: 'CCTV Cameras',
    rating: 4.5,
    reviews: 218,
    description: '1080p HD, motion detection, two-way audio, solar-powered option',
  },
  {
    id: 3,
    name: 'SmartBell Pro Video Doorbell',
    price: 149.99,
    originalPrice: 199.99,
    image: '/assets/images/doorbell-product.png',
    alt: 'SmartBell Pro video doorbell with HD camera and two-way audio',
    category: 'Doorbells',
    badge: 'New',
    rating: 4.9,
    reviews: 512,
    description: 'HD 1080p, instant alerts, two-way talk, works with Alexa & Google Home',
  },
  {
    id: 4,
    name: 'DoorGuard Slim Doorbell',
    price: 79.99,
    image: '/assets/images/doorbell-product.png',
    alt: 'DoorGuard slim video doorbell with motion detection and night vision',
    category: 'Doorbells',
    rating: 4.4,
    reviews: 189,
    description: 'Slim design, 160° field of view, night vision, battery or wired',
  },
  {
    id: 5,
    name: 'Universal Mount Kit',
    price: 24.99,
    image: '/assets/images/accessories-product.png',
    alt: 'Universal security camera mounting kit with brackets and hardware',
    category: 'Accessories',
    rating: 4.6,
    reviews: 95,
    description: 'Adjustable brackets, stainless hardware, fits all major camera brands',
  },
  {
    id: 6,
    name: 'PoE Network Switch 8-Port',
    price: 59.99,
    originalPrice: 79.99,
    image: '/assets/images/accessories-product.png',
    alt: '8-port PoE network switch for powering multiple security cameras',
    category: 'Accessories',
    badge: 'Sale',
    rating: 4.7,
    reviews: 143,
    description: '8-port PoE+, 120W total power, plug-and-play, metal housing',
  },
];

const categories = ['All', 'CCTV Cameras', 'Doorbells', 'Accessories'];

function StarRating({ rating }: { rating: number }) {
  return (
    <div style={{ display: 'flex', gap: '2px' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill={star <= Math.round(rating) ? '#f59e0b' : '#e5e7eb'}
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

export default function EcommerceStore() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [addedId, setAddedId] = useState<number | null>(null);
  const [bannerVisible, setBannerVisible] = useState(true);

  const filtered = activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) {
        return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  const removeFromCart = (id: number) => {
    setCart(prev => prev.filter(i => i.id !== id));
  };

  const scrollToProducts = () => {
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

        .eco-root {
          font-family: 'DM Sans', sans-serif;
          background: #f8f9fb;
          min-height: 100vh;
          color: #1a1a2e;
        }

        /* PROMO BANNER */
        .eco-promo-banner {
          background: linear-gradient(90deg, #0d1b2a 0%, #0077b6 40%, #00b4d8 70%, #0077b6 100%);
          background-size: 200% 100%;
          animation: bannerShift 6s ease infinite;
          padding: 12px 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          position: relative;
        }
        @keyframes bannerShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .eco-promo-banner-content {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .eco-promo-tag {
          background: #f59e0b;
          color: #0d1b2a;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          padding: 4px 10px;
          border-radius: 6px;
          flex-shrink: 0;
        }
        .eco-promo-text {
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.2px;
        }
        .eco-promo-text strong { color: #fde68a; }
        .eco-promo-cta {
          background: #fff;
          color: #0d1b2a;
          font-size: 12px;
          font-weight: 700;
          padding: 6px 16px;
          border-radius: 99px;
          text-decoration: none;
          transition: transform 0.15s, background 0.2s;
          white-space: nowrap;
          cursor: pointer;
          border: none;
          font-family: 'DM Sans', sans-serif;
        }
        .eco-promo-cta:hover { transform: scale(1.04); background: #f0f9ff; }
        .eco-promo-close {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          font-size: 18px;
          line-height: 1;
          padding: 4px;
          transition: color 0.2s;
        }
        .eco-promo-close:hover { color: #fff; }

        /* NAV */
        .eco-nav {
          background: #0d1b2a;
          position: sticky;
          top: 0;
          z-index: 100;
          box-shadow: 0 2px 20px rgba(0,0,0,0.3);
        }
        .eco-nav-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 32px;
          height: 68px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .eco-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
        }
        .eco-logo-icon {
          width: 38px;
          height: 38px;
          background: linear-gradient(135deg, #00b4d8, #0077b6);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .eco-logo-text {
          font-size: 20px;
          font-weight: 700;
          color: #ffffff;
          letter-spacing: -0.3px;
        }
        .eco-logo-text span { color: #00b4d8; }
        .eco-nav-links {
          display: flex;
          gap: 32px;
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .eco-nav-links a {
          color: #c9d1d9;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: color 0.2s;
        }
        .eco-nav-links a:hover { color: #00b4d8; }
        .eco-nav-right { display: flex; align-items: center; gap: 16px; }
        .eco-cart-btn {
          position: relative;
          background: #00b4d8;
          border: none;
          border-radius: 10px;
          padding: 10px 18px;
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s, transform 0.15s;
          font-family: 'DM Sans', sans-serif;
        }
        .eco-cart-btn:hover { background: #0096c7; transform: translateY(-1px); }
        .eco-cart-badge {
          position: absolute;
          top: -6px;
          right: -6px;
          background: #ef4444;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* HERO */
        .eco-hero {
          background: linear-gradient(135deg, #0d1b2a 0%, #1a3a5c 50%, #0d1b2a 100%);
          padding: 80px 32px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .eco-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 30% 50%, rgba(0,180,216,0.15) 0%, transparent 60%),
                      radial-gradient(ellipse at 70% 50%, rgba(0,119,182,0.1) 0%, transparent 60%);
        }
        .eco-hero-content { position: relative; max-width: 700px; margin: 0 auto; }
        .eco-hero-badge {
          display: inline-block;
          background: rgba(0,180,216,0.15);
          border: 1px solid rgba(0,180,216,0.4);
          color: #00b4d8;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          padding: 6px 16px;
          border-radius: 99px;
          margin-bottom: 20px;
        }
        .eco-hero h1 {
          font-size: 52px;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.15;
          margin: 0 0 16px;
          letter-spacing: -1px;
        }
        .eco-hero h1 span { color: #00b4d8; }
        .eco-hero p {
          font-size: 17px;
          color: #8b9ab0;
          line-height: 1.7;
          margin: 0 0 36px;
        }
        .eco-hero-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #00b4d8;
          color: #fff;
          padding: 16px 36px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          text-decoration: none;
          transition: background 0.2s, transform 0.15s;
          cursor: pointer;
          border: none;
          font-family: 'DM Sans', sans-serif;
        }
        .eco-hero-cta:hover { background: #0096c7; transform: translateY(-2px); }

        /* STATS BAR */
        .eco-stats {
          background: #ffffff;
          border-bottom: 1px solid #e8ecf0;
        }
        .eco-stats-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 32px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
        }
        .eco-stat {
          padding: 24px 20px;
          text-align: center;
          border-right: 1px solid #e8ecf0;
        }
        .eco-stat:last-child { border-right: none; }
        .eco-stat-num {
          font-size: 28px;
          font-weight: 700;
          color: #0d1b2a;
          display: block;
        }
        .eco-stat-label {
          font-size: 13px;
          color: #6b7280;
          margin-top: 2px;
        }

        /* PRODUCTS SECTION */
        .eco-products-section {
          max-width: 1280px;
          margin: 0 auto;
          padding: 60px 32px;
        }
        .eco-section-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          margin-bottom: 36px;
        }
        .eco-section-title {
          font-size: 32px;
          font-weight: 700;
          color: #0d1b2a;
          margin: 0 0 6px;
          letter-spacing: -0.5px;
        }
        .eco-section-sub {
          font-size: 15px;
          color: #6b7280;
          margin: 0;
        }

        /* CATEGORY TABS */
        .eco-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .eco-tab {
          padding: 8px 20px;
          border-radius: 99px;
          border: 1.5px solid #e0e4ea;
          background: #fff;
          color: #4b5563;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'DM Sans', sans-serif;
        }
        .eco-tab:hover { border-color: #00b4d8; color: #00b4d8; }
        .eco-tab.active {
          background: #0d1b2a;
          border-color: #0d1b2a;
          color: #fff;
        }

        /* PRODUCT GRID */
        .eco-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .eco-card {
          background: #ffffff;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #e8ecf0;
          transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s;
          position: relative;
        }
        .eco-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 40px rgba(13,27,42,0.12);
        }
        .eco-card-badge {
          position: absolute;
          top: 14px;
          left: 14px;
          background: #0d1b2a;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
          padding: 4px 10px;
          border-radius: 6px;
          z-index: 2;
        }
        .eco-card-badge.sale { background: #ef4444; }
        .eco-card-badge.new { background: #10b981; }
        .eco-card-img {
          width: 100%;
          height: 220px;
          object-fit: contain;
          background: #f0f4f8;
          padding: 20px;
          display: block;
        }
        .eco-card-body { padding: 20px; }
        .eco-card-cat {
          font-size: 11px;
          font-weight: 600;
          color: #00b4d8;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 6px;
        }
        .eco-card-name {
          font-size: 17px;
          font-weight: 700;
          color: #0d1b2a;
          margin: 0 0 8px;
          line-height: 1.3;
        }
        .eco-card-desc {
          font-size: 13px;
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 14px;
        }
        .eco-card-rating {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 16px;
        }
        .eco-card-rating-text {
          font-size: 12px;
          color: #9ca3af;
        }
        .eco-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .eco-price-wrap { display: flex; align-items: baseline; gap: 8px; }
        .eco-price {
          font-size: 22px;
          font-weight: 700;
          color: #0d1b2a;
        }
        .eco-price-orig {
          font-size: 14px;
          color: #9ca3af;
          text-decoration: line-through;
        }
        .eco-add-btn {
          background: #0d1b2a;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 10px 18px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
          font-family: 'DM Sans', sans-serif;
          white-space: nowrap;
        }
        .eco-add-btn:hover { background: #00b4d8; transform: scale(1.04); }
        .eco-add-btn.added { background: #10b981; }

        /* FEATURES STRIP */
        .eco-features {
          background: #0d1b2a;
          padding: 48px 32px;
        }
        .eco-features-inner {
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 32px;
        }
        .eco-feature {
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }
        .eco-feature-icon {
          width: 48px;
          height: 48px;
          background: rgba(0,180,216,0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .eco-feature-title {
          font-size: 15px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 4px;
        }
        .eco-feature-desc {
          font-size: 13px;
          color: #8b9ab0;
          margin: 0;
          line-height: 1.5;
        }

        /* CART DRAWER */
        .eco-cart-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 200;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s;
        }
        .eco-cart-overlay.open { opacity: 1; pointer-events: all; }
        .eco-cart-drawer {
          position: fixed;
          top: 0;
          right: 0;
          width: 400px;
          height: 100vh;
          background: #fff;
          z-index: 201;
          transform: translateX(100%);
          transition: transform 0.35s cubic-bezier(0.4,0,0.2,1);
          display: flex;
          flex-direction: column;
          box-shadow: -8px 0 40px rgba(0,0,0,0.15);
        }
        .eco-cart-drawer.open { transform: translateX(0); }
        .eco-cart-header {
          padding: 24px;
          border-bottom: 1px solid #e8ecf0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .eco-cart-title {
          font-size: 20px;
          font-weight: 700;
          color: #0d1b2a;
          margin: 0;
        }
        .eco-cart-close {
          background: none;
          border: none;
          cursor: pointer;
          color: #6b7280;
          padding: 4px;
          border-radius: 6px;
          transition: background 0.2s;
        }
        .eco-cart-close:hover { background: #f3f4f6; }
        .eco-cart-items { flex: 1; overflow-y: auto; padding: 16px 24px; }
        .eco-cart-item {
          display: flex;
          gap: 14px;
          padding: 16px 0;
          border-bottom: 1px solid #f3f4f6;
        }
        .eco-cart-item-img {
          width: 64px;
          height: 64px;
          object-fit: contain;
          background: #f0f4f8;
          border-radius: 10px;
          padding: 6px;
          flex-shrink: 0;
        }
        .eco-cart-item-info { flex: 1; }
        .eco-cart-item-name {
          font-size: 14px;
          font-weight: 600;
          color: #0d1b2a;
          margin: 0 0 4px;
        }
        .eco-cart-item-price {
          font-size: 14px;
          color: #00b4d8;
          font-weight: 700;
        }
        .eco-cart-item-qty {
          font-size: 12px;
          color: #9ca3af;
          margin-top: 2px;
        }
        .eco-cart-remove {
          background: none;
          border: none;
          cursor: pointer;
          color: #ef4444;
          font-size: 18px;
          padding: 0;
          line-height: 1;
          align-self: flex-start;
        }
        .eco-cart-empty {
          text-align: center;
          padding: 60px 20px;
          color: #9ca3af;
        }
        .eco-cart-empty svg { margin-bottom: 16px; }
        .eco-cart-footer {
          padding: 24px;
          border-top: 1px solid #e8ecf0;
        }
        .eco-cart-total {
          display: flex;
          justify-content: space-between;
          font-size: 18px;
          font-weight: 700;
          color: #0d1b2a;
          margin-bottom: 16px;
        }
        .eco-checkout-btn {
          width: 100%;
          background: #00b4d8;
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 16px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s;
          font-family: 'DM Sans', sans-serif;
        }
        .eco-checkout-btn:hover { background: #0096c7; }

        /* FOOTER */
        .eco-footer {
          background: #0d1b2a;
          padding: 48px 32px 24px;
          color: #8b9ab0;
        }
        .eco-footer-inner {
          max-width: 1280px;
          margin: 0 auto;
        }
        .eco-footer-top {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 48px;
          padding-bottom: 40px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          margin-bottom: 24px;
        }
        .eco-footer-brand p {
          font-size: 14px;
          line-height: 1.7;
          margin: 12px 0 0;
          max-width: 280px;
        }
        .eco-footer-brand-domain {
          font-size: 13px;
          color: #00b4d8;
          margin: 8px 0 0;
          font-weight: 600;
          letter-spacing: 0.3px;
        }
        .eco-footer-disclaimer {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 8px;
          padding: 16px 20px;
          margin-bottom: 24px;
          font-size: 12px;
          line-height: 1.7;
          color: #6b7a8d;
        }
        .eco-footer-disclaimer strong {
          color: #8b9ab0;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-right: 4px;
        }
        .eco-footer-col h4 {
          font-size: 14px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 16px;
          letter-spacing: 0.3px;
        }
        .eco-footer-col ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .eco-footer-col ul li a {
          color: #8b9ab0;
          text-decoration: none;
          font-size: 14px;
          transition: color 0.2s;
        }
        .eco-footer-col ul li a:hover { color: #00b4d8; }
        .eco-footer-bottom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 13px;
          flex-wrap: wrap;
          gap: 12px;
        }
        .eco-footer-bottom-links {
          display: flex;
          gap: 20px;
        }
        .eco-footer-bottom-links a {
          color: #8b9ab0;
          text-decoration: none;
          font-size: 13px;
          transition: color 0.2s;
        }
        .eco-footer-bottom-links a:hover { color: #00b4d8; }
      `}</style>

      <div className="eco-root">
        {/* PROMO BANNER */}
        {bannerVisible && (
          <div className="eco-promo-banner">
            <div className="eco-promo-banner-content">
              <span className="eco-promo-tag">🔥 Limited Time</span>
              <span className="eco-promo-text">
                Free shipping on orders over <strong>$99</strong> · Up to <strong>30% off</strong> on CCTV bundles this week only
              </span>
              <button className="eco-promo-cta" onClick={scrollToProducts}>
                Shop the Sale →
              </button>
            </div>
            <button className="eco-promo-close" onClick={() => setBannerVisible(false)} aria-label="Close banner">×</button>
          </div>
        )}

        {/* NAV */}
        <nav className="eco-nav">
          <div className="eco-nav-inner">
            <Link href="/" className="eco-logo">
              <div className="eco-logo-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="4" fill="#fff" />
                  <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" fill="none" />
                  <path d="M12 3v2M12 19v2M3 12h2M19 12h2" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <span className="eco-logo-text">Cam<span>Smart</span></span>
            </Link>
            <ul className="eco-nav-links">
              <li><Link href="/">Home</Link></li>
              <li>
                <a href="#products" onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  CCTV Cameras
                </a>
              </li>
              <li>
                <a href="#products" onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  Doorbells
                </a>
              </li>
              <li>
                <a href="#products" onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  Accessories
                </a>
              </li>
              <li><Link href="/disclaimer">Support</Link></li>
            </ul>
            <div className="eco-nav-right">
              <button className="eco-cart-btn" onClick={() => setCartOpen(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                Cart
                {cartCount > 0 && <span className="eco-cart-badge">{cartCount}</span>}
              </button>
            </div>
          </div>
        </nav>

        {/* HERO */}
        <section className="eco-hero">
          <div className="eco-hero-content">
            <span className="eco-hero-badge">🔒 Trusted Security Solutions</span>
            <h1>Protect What <span>Matters Most</span></h1>
            <p>Professional-grade CCTV cameras, smart doorbells, and accessories — built for homes and businesses that demand real security.</p>
            <button className="eco-hero-cta" onClick={scrollToProducts}>
              Shop Now
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </section>

        {/* STATS */}
        <div className="eco-stats">
          <div className="eco-stats-inner">
            {[
              { num: '50,000+', label: 'Cameras Installed' },
              { num: '4.8★', label: 'Average Rating' },
              { num: '24/7', label: 'Customer Support' },
              { num: '2-Year', label: 'Warranty on All Products' },
            ].map((s) => (
              <div key={s.label} className="eco-stat">
                <span className="eco-stat-num">{s.num}</span>
                <span className="eco-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* PRODUCTS */}
        <section className="eco-products-section" id="products">
          <div className="eco-section-header">
            <div>
              <h2 className="eco-section-title">Our Products</h2>
              <p className="eco-section-sub">Professional security equipment for every need</p>
            </div>
            <div className="eco-tabs">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`eco-tab${activeCategory === cat ? ' active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="eco-grid">
            {filtered.map(product => (
              <div key={product.id} className="eco-card">
                {product.badge && (
                  <span className={`eco-card-badge${product.badge === 'Sale' ? ' sale' : product.badge === 'New' ? ' new' : ''}`}>
                    {product.badge}
                  </span>
                )}
                <img src={product.image} alt={product.alt} className="eco-card-img" />
                <div className="eco-card-body">
                  <div className="eco-card-cat">{product.category}</div>
                  <h3 className="eco-card-name">{product.name}</h3>
                  <p className="eco-card-desc">{product.description}</p>
                  <div className="eco-card-rating">
                    <StarRating rating={product.rating} />
                    <span className="eco-card-rating-text">{product.rating} ({product.reviews} reviews)</span>
                  </div>
                  <div className="eco-card-footer">
                    <div className="eco-price-wrap">
                      <span className="eco-price">${product.price.toFixed(2)}</span>
                      {product.originalPrice && (
                        <span className="eco-price-orig">${product.originalPrice.toFixed(2)}</span>
                      )}
                    </div>
                    <button
                      className={`eco-add-btn${addedId === product.id ? ' added' : ''}`}
                      onClick={() => addToCart(product)}
                    >
                      {addedId === product.id ? '✓ Added' : 'Add to Cart'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FEATURES */}
        <div className="eco-features">
          <div className="eco-features-inner">
            {[
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b4d8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                ),
                title: 'Free Shipping Over $99',
                desc: 'Fast delivery on all orders above $99 to your door',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b4d8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                ),
                title: '2-Year Warranty',
                desc: 'All products backed by our comprehensive warranty',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b4d8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                ),
                title: '24/7 Expert Support',
                desc: 'Our security specialists are always available to help',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b4d8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
                  </svg>
                ),
                title: '30-Day Returns',
                desc: 'Not satisfied? Return any product within 30 days',
              },
            ].map((f) => (
              <div key={f.title} className="eco-feature">
                <div className="eco-feature-icon">{f.icon}</div>
                <div>
                  <p className="eco-feature-title">{f.title}</p>
                  <p className="eco-feature-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FOOTER */}
        <footer className="eco-footer">
          <div className="eco-footer-inner">
            <div className="eco-footer-top">
              <div className="eco-footer-brand">
                <Link href="/" className="eco-logo">
                  <div className="eco-logo-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="4" fill="#fff" />
                      <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                  <span className="eco-logo-text">Cam<span>Smart</span></span>
                </Link>
                <p>Professional security solutions for homes and businesses. Trusted by over 50,000 customers nationwide.</p>
                <p className="eco-footer-brand-domain">🌐 camsmartstore.online</p>
              </div>
              <div className="eco-footer-col">
                <h4>Products</h4>
                <ul>
                  <li><a href="#products" onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); setActiveCategory('CCTV Cameras'); }}>CCTV Cameras</a></li>
                  <li><a href="#products" onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); setActiveCategory('Doorbells'); }}>Video Doorbells</a></li>
                  <li><a href="#products" onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); setActiveCategory('Accessories'); }}>Accessories</a></li>
                  <li><a href="#products" onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); setActiveCategory('All'); }}>Bundles</a></li>
                </ul>
              </div>
              <div className="eco-footer-col">
                <h4>Support</h4>
                <ul>
                  <li><Link href="/disclaimer">Installation Guide</Link></li>
                  <li><Link href="/disclaimer">FAQs</Link></li>
                  <li><Link href="/refund-policy">Warranty</Link></li>
                  <li><Link href="/disclaimer">Contact Us</Link></li>
                </ul>
              </div>
              <div className="eco-footer-col">
                <h4>Company</h4>
                <ul>
                  <li><Link href="/disclaimer">About Us</Link></li>
                  <li><Link href="/privacy-policy">Privacy Policy</Link></li>
                  <li><Link href="/terms-of-service">Terms of Service</Link></li>
                  <li><Link href="/refund-policy">Refund Policy</Link></li>
                </ul>
              </div>
            </div>
            <div className="eco-footer-disclaimer">
              <strong>Disclaimer:</strong> CamSmart (camsmartstore.online) is an independent retailer of security cameras, doorbells, and related accessories. We are not affiliated with, endorsed by, or officially connected to any third-party brand or manufacturer unless explicitly stated. Product names, logos, and trademarks mentioned on this website belong to their respective owners. Prices, availability, and specifications are subject to change without notice. CamSmart makes no warranties, express or implied, regarding the accuracy or completeness of the information provided on this site. Use of our products and services is subject to our <Link href="/terms-of-service" style={{color:'#00b4d8', textDecoration:'underline'}}>Terms of Service</Link> and <Link href="/privacy-policy" style={{color:'#00b4d8', textDecoration:'underline'}}>Privacy Policy</Link>. For questions, contact us through our support page.
            </div>
            <div className="eco-footer-bottom">
              <span>© 2026 CamSmart (camsmartstore.online). All rights reserved.</span>
              <div className="eco-footer-bottom-links">
                <Link href="/privacy-policy">Privacy Policy</Link>
                <Link href="/terms-of-service">Terms of Service</Link>
                <Link href="/disclaimer">Disclaimer</Link>
                <Link href="/refund-policy">Refund Policy</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* CART DRAWER */}
      <div className={`eco-cart-overlay${cartOpen ? ' open' : ''}`} onClick={() => setCartOpen(false)} />
      <div className={`eco-cart-drawer${cartOpen ? ' open' : ''}`}>
        <div className="eco-cart-header">
          <h2 className="eco-cart-title">Your Cart ({cartCount})</h2>
          <button className="eco-cart-close" onClick={() => setCartOpen(false)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="eco-cart-items">
          {cart.length === 0 ? (
            <div className="eco-cart-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              <p>Your cart is empty</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.id} className="eco-cart-item">
                <img src={item.image} alt={item.alt} className="eco-cart-item-img" />
                <div className="eco-cart-item-info">
                  <p className="eco-cart-item-name">{item.name}</p>
                  <p className="eco-cart-item-price">${item.price.toFixed(2)}</p>
                  <p className="eco-cart-item-qty">Qty: {item.quantity}</p>
                </div>
                <button className="eco-cart-remove" onClick={() => removeFromCart(item.id)}>×</button>
              </div>
            ))
          )}
        </div>
        {cart.length > 0 && (
          <div className="eco-cart-footer">
            <div className="eco-cart-total">
              <span>Total</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            <button className="eco-checkout-btn">Proceed to Checkout</button>
          </div>
        )}
      </div>
    </>
  );
}
